export const user = {
	common: {
		deleted: "Deleted User",
		username: "User ID",
		fullname: "Fullname",
		email: "Email",
		role: "Role",
		active: "Active",
		password: "Password",
	},
	role: {
		ADMINISTRATOR: "Administrator",
		INSTALLER: "Installer",
		OWNER: "Owner",
		TENANT: "Tenant",
		none: "No Role",
	},
	validation: {
		username: {
			pattern: "Username can only contain letters, numbers, at sign (@), plus (+), period (.), underscore (_) and hyphen (-)",
		},
		fullname: {
			pattern: "Fullname can only contain letters, spaces and hyphens (-)",
		},
		email: {
			pattern: "Please enter a valid email address",
		},
		emailConfirm: {
			pattern: "Please enter a valid email address",
			match: "Value must match the email entered previously",
		},
	},
	management: {
		pageTitle: "User Management",
		actions: {
			details: "View Details",
			create: "Create User",
			addExisting: "Add Existing User",
		},
	},
	addExisting: {
		header: "Add Existing User to Scheme",
		message:
			"If this person already uses the $t(common.appName) in another scheme, enter their email address to give them access to this scheme as well. They keep their existing username, full name and password.",
		form: {
			email: {
				label: "Email",
				placeholder: "Their existing email address",
			},
			role: {
				label: "Role in this scheme",
				placeholder: "Select a role",
			},
		},
		actions: {
			submit: "Add to Scheme",
			createInstead: "Create New User Instead",
		},
		loading: "Adding User to Scheme",
		notFound:
			"No $t(common.appName) user has this email address. Use Create New User Instead to set them up from scratch. Your entries are kept.",
		toast: {
			successSummary: "User Added to Scheme",
			successDetail: "{{email}} now has access to this scheme and has been emailed to let them know.",
			errorSummary: "Could Not Add User",
			errorDetail: "$t({{error}})",
			unknownDetail: "The request did not complete. Please refresh the page and try again.",
			rollbackSummary: "Add Not Completed",
			rollbackDetail:
				"The user's role could not be saved so the change was undone. Please try again. Error: {{error}}",
			rollbackFailedDetail:
				"The user's role could not be saved and the change could not be undone. Please contact support quoting {{email}}. Error: {{error}}",
		},
	},
	details: {
		pageTitle: "User Details",
		pageSubtitle: "User ID: {{username}}",
		actions: {
			edit: "Edit User",
			delete: "Delete User",
			reset: "Reset Password",
			resend: "Resend Invite",
		},
		form: {
			status: "Status",
			mfa: "MFA",
			login: {
				last: "Last Login Attempt",
				lastSuccess: "Last Successful Login",
			},
		},
		status: {
			CONFIRMED: "Registration Complete",
			FORCE_CHANGE_PASSWORD: "Registration Incomplete",
			RESET_REQUIRED: "Password Reset Required",
			EMAIL_UNVERIFIED: "Email Not Verified",
		},
		tenant: {
			zones: {
				header: "Assigned Zones",
				childHeader: "Child Zone Devices",
			},
		},
	},
	create: {
		pageTitle: "Create User",
		loading: "Creating New User",
		actions: {
			submit: "Create User",
		},
		form: {
			fullname: {
				label: "Fullname",
				pattern: "Fullname can only contain letters, spaces and hyphens (-)",
			},
			email: {
				label: "Email",
				pattern: "Please enter a valid email address",
			},
			emailConfirm: {
				label: "Confirm Email",
				pattern: "Please enter a valid email address",
				match: "Value must match the email entered previously",
			},
			role: {
				label: "Role",
				placeholder: "Select a role",
			},
			active: {
				label: "Active",
			},
		},
		toast: {
			successSummary: "User Create Success",
			successDetail: "A welcome email has been sent to {{email}} with registration details.",
			errorSummary: "User Create Error",
			errorDetail: 'Could not create the user. $t({{error}}, {"user": {{data}} })',
		},
		dupeUser: {
			header: "User Already Exists",
			message:
				"A user with the email {{email}} already exists assigned to a different scheme. Would you like to import this user into your scheme. The user will be imported with their current username and fullname but be assigned a new role for this scheme.",
			warning: "If you choose no, you will not be able to create a new user with this email",
			accept: "Import",
			reject: "No",
			loading: "Importing User to Project",
		},
	},
	edit: {
		pageTitle: "Edit User",
		pageSubtitle: "User ID: {{username}}",
		loading: "Updating User Details",
		actions: {
			submit: "Update User",
		},
		toast: {
			successSummary: "User Update Success",
			successDetail: "",
			errorSummary: "User Update Error",
			errorDetail: 'Could not update the user. $t({{error}}, {"user": {{data}} })',
		},
	},
	delete: {
		header: "Confirm Delete User",
		message:
			"Are you sure you want to delete the user {{user}}? The user will no longer be able to login or use features associated with the $t(common.appName).",
		loading: "Deleting User",
		accept: "Delete User",
		reject: "Cancel",
		toast: {
			successSummary: "Delete Success",
			successDetail:
				"{{user}} will no longer be able to login or use the features associated with the $t(common.appName).",
			errorSummary: "Delete Failed",
			errorDetail: "Please refresh the page and try again.",
		},
	},
	error: {
		usernameGroupExists: "A user with username {{user.username}} already exists.",
		emailGroupExists: "A user with email {{user.email}} already exists",
		usernameExists: "A user with username {{user.username}} already exists.",
		emailExists: "A user with email {{user.email}} already exists",
		importNotFound: "$t(user.addExisting.notFound)",
		importIsAdmin: "This email belongs to a global Administrator, who already has access to every scheme.",
		importAlreadyInScheme: "This user already has access to this scheme.",
		rolesNotAllowed: "A user assigned the Clinician role cannot be assigned additional roles.",
	},
	mfa: {
		type: {
			SOFTWARE_TOKEN_MFA: "Enabled",
			NONE: "Not Enabled",
		},
	},
	resetPassword: {
		header: "Reset User Password",
		message:
			"Are you sure you want to reset the password for user {{user}}? This will send a reset code to {{email}} in order for the user to reset their password.",
		loading: "Resetting User Password",
		accept: "Reset Password",
		toast: {
			successSummary: "Reset Code Sent",
			successDetail: "",
			errorSummary: "Password Reset Error",
			errorDetail: "Could not reset the users password. {{error}}",
		},
	},
	resendInvite: {
		header: "Resend Registration Email",
		message:
			"This will resend an email to {{email}} for user {{user}} to complete registration. The user will have {{expires}} to complete registration before the link expires.",
		loading: "Sending Registration",
		accept: "Resend Invite",
		toast: {
			successSummary: "Registration Email Sent",
			successDetail: "",
			errorSummary: "Resend Invite Failed",
			errorDetail: "Could not resend an registraion email to the user. {{error}}",
		},
	},
};
